import {
  type CareerHistoryFormValues,
  normaliseCareerHistory,
} from '@seek/ca-myprofile-utils';
import { CareerHistoryForm } from '@seek/profile-edit-ui';
import React, { useState } from 'react';

import { onSubmitFail } from 'src/components/Card/onSubmitFail';
import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { CAREER_HISTORY } from 'src/components/Router';
import { useEnterReadMode } from 'src/components/Router/hooks';
import { EDIT, ADD } from 'src/consts';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import type { ActionName } from 'src/types';
import * as Schema from 'src/types/graphql';

import { useSuggestedSkills } from '../../Skills/data';
import {
  useAddUnconfirmedRole,
  useGetConfirmedRoles,
  useGetUnconfirmedRoles,
  useRejectUnconfirmedRole,
  useRemoveConfirmedRole,
  useUpdateConfirmedRoles,
} from '../data';

const WAIT_FOR_SUGGESTED_SKILLS_TO_REFRESH = 5000;

export const EditCareerHistory = ({ onFormDirtyChange }: DirtyFormProps) => {
  const enterReadMode = useEnterReadMode('career-history');
  const page = useTypedSelector((store) => store.router.page);
  const { zone, isUnifiedSite } = useAppConfig();
  const { confirmedRoles } = useGetConfirmedRoles();
  const { unconfirmedRoles } = useGetUnconfirmedRoles();
  const { refetch } = useSuggestedSkills();

  const id =
    useTypedSelector((store) => store.router[CAREER_HISTORY].params.id) || '';

  const activeRole = [...confirmedRoles, ...unconfirmedRoles].find(
    (role) => role.id === id,
  );
  const isUnconfirmed = Boolean(
    unconfirmedRoles.find((role) => role.id === id),
  );

  const [mode] = useState<ActionName>(activeRole ? EDIT : ADD);

  const initialValues = activeRole;
  const validateOnMount = isUnconfirmed;
  const canDelete = confirmedRoles.length > 1 || isUnconfirmed;

  const [updateConfirmedRoles] = useUpdateConfirmedRoles(mode);
  const [removeConfirmedRole] = useRemoveConfirmedRole();
  const [addUnconfirmedRole] = useAddUnconfirmedRole(EDIT);
  const [rejectUnconfirmedRole] = useRejectUnconfirmedRole();

  const refetchSuggestedSkills = () => {
    setTimeout(() => {
      refetch();
    }, WAIT_FOR_SUGGESTED_SKILLS_TO_REFRESH);
  };

  const submitForm = (params: {
    dirty: boolean;
    values: CareerHistoryFormValues;
  }) => {
    const { values, dirty } = params;
    if (isUnconfirmed) {
      addUnconfirmedRole({
        role: normaliseCareerHistory(values) as Schema.ConfirmRoleInput,
      });
    } else if (dirty) {
      updateConfirmedRoles({
        role: normaliseCareerHistory(values),
      });
    }
    refetchSuggestedSkills();
    enterReadMode();
  };

  const deleteCareerRole = () => {
    if (isUnconfirmed) {
      const selectedRole = unconfirmedRoles.find((role) => role.id === id);
      rejectUnconfirmedRole({
        role: {
          id,
          dataType: Schema.UnconfirmedDataType.CareerHistory,
          tracking: selectedRole?.tracking || null,
        },
      });
    } else {
      removeConfirmedRole({ input: { id } });
      refetchSuggestedSkills();
    }
    enterReadMode();
  };

  const onSubmitFailFn = (errors: string[]) => {
    onSubmitFail(errors, page);
  };

  const achievementsRef = useFocus<HTMLTextAreaElement>({
    card: 'career-history',
    fieldName: 'achievements',
  });

  const isEdit = mode === EDIT;

  return (
    <CareerHistoryForm
      zone={zone}
      initialValues={initialValues}
      onSubmit={submitForm}
      onCancel={enterReadMode}
      onSubmitFail={onSubmitFailFn}
      achievementsRef={achievementsRef}
      onFormDirtyChange={onFormDirtyChange}
      validateOnMount={validateOnMount}
      scrollToTopOnMobile={false}
      isUnifiedSite={isUnifiedSite}
      {...(isEdit && canDelete && { onDelete: deleteCareerRole })}
    />
  );
};
